import { isValid, parse } from 'date-fns';
import { enUS, es } from 'date-fns/locale';

const moneyFormatter = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthFormatters = {
  en: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }),
  es: new Intl.DateTimeFormat('es-MX', { month: 'short', timeZone: 'UTC' })
};

export function formatBalanceMoney(value: number | string | null | undefined): string {
  if (value == null || (typeof value === 'string' && !value.trim())) return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return `${amount < 0 ? '-$' : '$'}${moneyFormatter.format(Math.abs(amount))}`;
}

export function parseBalanceApiDate(value: string | null | undefined): Date | null {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/u);
  if (!match) return null;
  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
}

export function formatBalanceDate(
  value: string | Date | null | undefined,
  language: string,
  kind: 'date' | 'period' | 'month' = 'date'
): string {
  // API calendar dates must not shift with the browser's timezone. Date controls use local calendar fields.
  const date =
    value instanceof Date
      ? new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()))
      : parseBalanceApiDate(value);
  if (!date || !isValid(date)) return '';
  const month = monthFormatters[language.toLowerCase().startsWith('es') ? 'es' : 'en'].format(date).replace(/\.$/u, '');
  if (kind === 'month') return month;
  const period = `${month} ${date.getUTCFullYear()}`;
  return kind === 'period' ? period : `${String(date.getUTCDate()).padStart(2, '0')} ${period}`;
}

export function formatBalanceTransactionDate(
  display: string | null | undefined,
  timestamp: string | null | undefined,
  language: string
): string {
  if (!display?.trim()) return formatBalanceDate(timestamp, language);
  const isoDate = parseBalanceApiDate(display.trim());
  if (isoDate) return formatBalanceDate(display.trim(), language);

  // Keep the backend's chosen transaction date, including when it differs from the raw timestamp.
  const patterns = /\d{4}$/u.test(display.trim()) ? ['dd MMM yyyy', 'dd MMMM yyyy'] : ['dd MMM yy'];
  const referenceDate = parseBalanceApiDate(timestamp) ?? new Date();
  for (const locale of [enUS, es]) {
    for (const pattern of patterns) {
      const date = parse(display.trim(), pattern, referenceDate, { locale });
      if (isValid(date)) return formatBalanceDate(date, language);
    }
  }
  return display;
}
